import { useMemo, useState } from 'react';
import {
  makeStyles,
  tokens,
  Body1,
  Body2,
  Caption1,
  Input,
  Dropdown,
  Option,
  Badge,
  Tooltip,
} from '@fluentui/react-components';
import { PageTitle } from '@/components/PageTitle';
import { Search24Regular } from '@fluentui/react-icons';
import { useSharePointList } from '@/hooks/useSharePointList';
import type {
  Employee,
  Project,
  Allocation,
} from '@/models/types';
import { useAppContext } from '@/store/appContext';
import { canViewAllAllocations } from '@/utils/permissions';
import { AccessDenied } from '@/components/AccessDenied';
import { formatPercent } from '@/utils/formatters';

const useStyles = makeStyles({
  page: { display: 'flex', flexDirection: 'column', gap: '20px' },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap' as const,
    gap: '12px',
  },
  toolbar: {
    display: 'flex',
    gap: '12px',
    flexWrap: 'wrap' as const,
    alignItems: 'center',
  },
  outer: {
    position: 'relative' as const,
  },
  wrapper: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusLarge,
    boxShadow: tokens.shadow4,
    overflow: 'auto',
    maxHeight: 'calc(100vh - 260px)',
    WebkitOverflowScrolling: 'touch' as const,
  },
  scrollHint: {
    position: 'absolute' as const,
    top: 0,
    right: 0,
    bottom: 0,
    width: '24px',
    pointerEvents: 'none' as const,
    background: `linear-gradient(to right, transparent, ${tokens.colorNeutralBackground1})`,
    borderTopRightRadius: tokens.borderRadiusLarge,
    borderBottomRightRadius: tokens.borderRadiusLarge,
    '@media (min-width: 900px)': {
      display: 'none',
    },
  },
  scrollNote: {
    display: 'none',
    '@media (max-width: 768px)': {
      display: 'block',
      padding: '6px 12px',
      fontSize: '11px',
      color: tokens.colorNeutralForeground3,
      textAlign: 'center' as const,
    },
  },
  table: {
    borderCollapse: 'collapse' as const,
    width: 'max-content',
    minWidth: '100%',
  },
  stickyHeader: {
    position: 'sticky' as const,
    top: 0,
    backgroundColor: tokens.colorNeutralBackground1,
    zIndex: 2,
  },
  stickyCol: {
    position: 'sticky' as const,
    left: 0,
    backgroundColor: tokens.colorNeutralBackground1,
    zIndex: 1,
  },
  th: {
    padding: '10px 12px',
    fontSize: '11px',
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
    borderBottom: `2px solid ${tokens.colorNeutralStroke1}`,
    whiteSpace: 'nowrap' as const,
  },
  thProj: {
    writingMode: 'vertical-lr' as const,
    transform: 'rotate(180deg)',
    height: '140px',
    verticalAlign: 'bottom' as const,
    padding: '8px 6px',
    fontSize: '11px',
    whiteSpace: 'nowrap' as const,
  },
  td: {
    padding: '4px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke3}`,
    textAlign: 'center' as const,
  },
  tdName: {
    padding: '8px 14px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke3}`,
    whiteSpace: 'nowrap' as const,
    minWidth: '220px',
  },
  cell: {
    width: '56px',
    height: '36px',
    borderRadius: tokens.borderRadiusMedium,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto',
    fontSize: '12px',
    fontWeight: 600,
  },
  summary: {
    padding: '8px 16px',
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    display: 'flex',
    gap: '24px',
    flexWrap: 'wrap' as const,
  },
});

function percentColor(percent: number): { bg: string; fg: string } {
  if (percent === 0) return { bg: 'transparent', fg: 'transparent' };
  if (percent < 25) return { bg: '#c8e6c9', fg: '#1b5e20' };
  if (percent < 50) return { bg: '#81c784', fg: '#ffffff' };
  if (percent < 75) return { bg: '#4caf50', fg: '#ffffff' };
  if (percent < 100) return { bg: '#2e7d32', fg: '#ffffff' };
  return { bg: '#1b5e20', fg: '#ffffff' };
}

export function AllocationMatrix() {
  const styles = useStyles();
  const { currentUser, listIds } = useAppContext();

  if (currentUser && !canViewAllAllocations(currentUser.role)) {
    return <AccessDenied reason="Allocation matrix is restricted to Admin and Reporting Officers." />;
  }

  const { items: employees } = useSharePointList<Employee>('OC_Employees', {
    autoFetch: !!listIds,
  });
  const { items: projects } = useSharePointList<Project>('OC_Projects', {
    autoFetch: !!listIds,
  });
  const { items: allocations } = useSharePointList<Allocation>(
    'OC_Allocations',
    { autoFetch: !!listIds },
  );

  const [search, setSearch] = useState('');
  const [deptFilter, setDeptFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('Active');

  const departments = useMemo(() => {
    const set = new Set<string>();
    for (const e of employees) if (e.fields.Department) set.add(e.fields.Department);
    return Array.from(set).sort();
  }, [employees]);

  const activeProjects = useMemo(() => {
    return projects
      .filter((p) => statusFilter === 'all' || p.fields.Status === statusFilter)
      .sort((a, b) => a.fields.Code.localeCompare(b.fields.Code));
  }, [projects, statusFilter]);

  const filteredEmployees = useMemo(() => {
    let res = employees.filter((e) => e.fields.Status === 'Active');
    if (deptFilter !== 'all') res = res.filter((e) => e.fields.Department === deptFilter);
    if (search) {
      const lower = search.toLowerCase();
      res = res.filter(
        (e) =>
          e.fields.Title.toLowerCase().includes(lower) ||
          e.fields.Email.toLowerCase().includes(lower),
      );
    }
    return res.sort((a, b) => a.fields.Title.localeCompare(b.fields.Title));
  }, [employees, deptFilter, search]);

  const allocationMap = useMemo(() => {
    const map = new Map<string, number>();
    for (const a of allocations) {
      map.set(`${a.fields.EmployeeId}:${a.fields.ProjectId}`, a.fields.AllocationPercent);
    }
    return map;
  }, [allocations]);

  const perEmployeeTotal = useMemo(() => {
    const map = new Map<string, number>();
    for (const e of employees) {
      const total = allocations
        .filter((a) => a.fields.EmployeeId === Number(e.id))
        .reduce((s, a) => s + (a.fields.AllocationPercent || 0), 0);
      map.set(e.id, total);
    }
    return map;
  }, [allocations, employees]);

  const perProjectTotal = useMemo(() => {
    const map = new Map<string, { total: number; count: number }>();
    for (const p of projects) {
      const projAllocs = allocations.filter((a) => a.fields.ProjectId === Number(p.id));
      map.set(p.id, {
        total: projAllocs.reduce((s, a) => s + (a.fields.AllocationPercent || 0), 0),
        count: projAllocs.length,
      });
    }
    return map;
  }, [allocations, projects]);

  const overallocated = Array.from(perEmployeeTotal.entries()).filter(
    ([, total]) => total > 100,
  ).length;
  const underallocated = filteredEmployees.filter(
    (e) => (perEmployeeTotal.get(e.id) ?? 0) < 50,
  ).length;

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <PageTitle>Allocation Matrix</PageTitle>
        <Caption1>
          {filteredEmployees.length} members × {activeProjects.length} projects
        </Caption1>
      </div>

      <div className={styles.toolbar}>
        <Input
          contentBefore={<Search24Regular />}
          placeholder="Search members..."
          value={search}
          onChange={(_, d) => setSearch(d.value)}
          style={{ minWidth: 220 }}
        />
        <Dropdown
          placeholder="Department"
          value={deptFilter === 'all' ? 'All Departments' : deptFilter}
          onOptionSelect={(_, d) => setDeptFilter(d.optionValue as string)}
          style={{ minWidth: 180 }}
        >
          <Option value="all">All Departments</Option>
          {departments.map((d) => (
            <Option key={d} value={d}>
              {d}
            </Option>
          ))}
        </Dropdown>
        <Dropdown
          placeholder="Project status"
          value={statusFilter === 'all' ? 'All projects' : statusFilter}
          onOptionSelect={(_, d) => setStatusFilter(d.optionValue as string)}
          style={{ minWidth: 160 }}
        >
          <Option value="all">All projects</Option>
          <Option value="Active">Active</Option>
          <Option value="On Hold">On Hold</Option>
          <Option value="Completed">Completed</Option>
        </Dropdown>
      </div>

      {filteredEmployees.length === 0 || activeProjects.length === 0 ? (
        <div
          style={{
            padding: 48,
            textAlign: 'center',
            backgroundColor: tokens.colorNeutralBackground1,
            borderRadius: tokens.borderRadiusLarge,
          }}
        >
          <Body1>
            {employees.length === 0
              ? 'No members found. Sync from Entra ID first.'
              : projects.length === 0
                ? 'No projects yet.'
                : 'No members match your filter.'}
          </Body1>
        </div>
      ) : (
        <>
        <Caption1 className={styles.scrollNote}>
          Swipe horizontally to see all projects →
        </Caption1>
        <div className={styles.outer}>
        <div className={styles.wrapper}>
          <table className={styles.table}>
            <thead>
              <tr className={styles.stickyHeader}>
                <th className={`${styles.th} ${styles.stickyCol}`} style={{ zIndex: 3 }}>
                  Employee
                </th>
                {activeProjects.map((p) => (
                  <Tooltip
                    key={p.id}
                    content={`${p.fields.Code} — ${p.fields.Title}`}
                    relationship="label"
                  >
                    <th className={`${styles.th} ${styles.thProj}`}>
                      {p.fields.Code}
                    </th>
                  </Tooltip>
                ))}
                <th className={styles.th}>Total</th>
              </tr>
            </thead>
            <tbody>
              {filteredEmployees.map((emp) => {
                const empId = Number(emp.id);
                const total = perEmployeeTotal.get(emp.id) ?? 0;
                return (
                  <tr key={emp.id}>
                    <td className={`${styles.tdName} ${styles.stickyCol}`}>
                      <Body2 style={{ fontWeight: 500 }}>{emp.fields.Title}</Body2>
                      <Caption1 style={{ display: 'block' }}>
                        {emp.fields.Department}
                      </Caption1>
                    </td>
                    {activeProjects.map((p) => {
                      const percent = allocationMap.get(`${empId}:${p.id}`) ?? 0;
                      const colors = percentColor(percent);
                      return (
                        <td key={p.id} className={styles.td}>
                          <Tooltip
                            content={
                              percent > 0
                                ? `${emp.fields.Title} → ${p.fields.Code}: ${percent}%`
                                : `No allocation`
                            }
                            relationship="description"
                          >
                            <div
                              className={styles.cell}
                              style={{
                                backgroundColor: colors.bg,
                                color: colors.fg,
                              }}
                            >
                              {percent > 0 ? formatPercent(percent) : ''}
                            </div>
                          </Tooltip>
                        </td>
                      );
                    })}
                    <td className={styles.td}>
                      <Badge
                        appearance="filled"
                        color={
                          total > 100
                            ? 'danger'
                            : total === 100
                              ? 'success'
                              : total < 50
                                ? 'warning'
                                : 'informative'
                        }
                      >
                        {formatPercent(total)}
                      </Badge>
                    </td>
                  </tr>
                );
              })}
              {/* Project totals row */}
              <tr style={{ backgroundColor: tokens.colorNeutralBackground2, fontWeight: 600 }}>
                <td className={`${styles.tdName} ${styles.stickyCol}`} style={{ backgroundColor: tokens.colorNeutralBackground2 }}>
                  <Body2 style={{ fontWeight: 700 }}>People on project</Body2>
                </td>
                {activeProjects.map((p) => {
                  const stat = perProjectTotal.get(p.id) ?? { total: 0, count: 0 };
                  return (
                    <td key={p.id} className={styles.td}>
                      <Caption1 style={{ fontWeight: 600 }}>
                        {stat.count}
                      </Caption1>
                    </td>
                  );
                })}
                <td />
              </tr>
            </tbody>
          </table>
        </div>
        <div className={styles.scrollHint} aria-hidden="true" />
        </div>
        </>
      )}

      <div className={styles.summary}>
        <Caption1>
          <strong>Overallocated (&gt;100%):</strong> {overallocated}
        </Caption1>
        <Caption1>
          <strong>Underallocated (&lt;50%):</strong> {underallocated}
        </Caption1>
        <Caption1>
          <strong>Total allocations:</strong> {allocations.length}
        </Caption1>
      </div>
    </div>
  );
}
